import { debugLog } from "../../../lib/terminalLogger";
import { api, APIError } from "../../../lib/api";
import { ErrorCode } from "../../../lib/errors";
import { ErrorCatalog } from "../../feedback/ErrorCatalog";

export interface BatchSummaryPayload {
	total: number;
	success: number;
	failed: number;
	cancelled: boolean;
	output_dir: string;
	duration_ms: number;
	hash_algorithm?: string | null;
}

export interface BatchItemPayload {
	index: number;
	objeto_id: string;
	guid?: string | null;
	nome?: string;
	page?: number;
	largura?: number | null;
	altura?: number | null;
	quantidade?: number;
	status: "success" | "error" | "cancelled" | string;
	error?: string | null;
	nome_arquivo_exportado?: string | null;
	caminho_exportado?: string | null;
	tamanho_arquivo?: number | null;
	md5_arquivo?: string | null;
	duration_ms?: number | null;
}

export interface BatchApiPayload {
	orc_id: number;
	oit_id: number;
	tipo_exportacao: string; // 'TIFF' | 'PNG' | 'PDF' | 'EPS'
	diretorio_destino: string;
	summary: BatchSummaryPayload;
	itens: BatchItemPayload[];
	idempotency_key?: string;
}

/**
 * Constrói o payload para POST /cdr/arquivos/exportar-batch a partir do resultado do C#
 */
export function buildBatchPayload(
	savedData: any,
	batchResult: any,
	format: string,
	outputDir: string,
	targets?: Array<{ staticId: string; guid?: string }>,
): BatchApiPayload | null {
	try {
		const orcId = Number(savedData?.pedido?.orc_id ?? 0) || 0;
		const oitId = Number(savedData?.pedido?.oit_id ?? 0) || 0;
		const summary = batchResult?.summary || {};

		const itensRaw: any[] = Array.isArray(batchResult?.items)
			? batchResult.items
			: [];
		const guidByStaticId: Record<string, string> = {};
		if (Array.isArray(targets)) {
			targets.forEach((t) => {
				if (t.staticId && t.guid)
					guidByStaticId[String(t.staticId)] = String(t.guid);
			});
		}
		// Mapa auxiliar de nomes a partir do savedData (quando disponível)
		const savedObjects: Array<{
			staticId?: string;
			id?: string;
			nome?: string;
		}> = Array.isArray(savedData?.objetos) ? savedData.objetos : [];
		const savedNameByStaticId: Record<string, string> = {};
		const savedDimByStaticId: Record<
			string,
			{ altura?: number; largura?: number; quantity?: number }
		> = {};
		for (const so of savedObjects) {
			const sid = String(so?.staticId ?? so?.id ?? "").trim();
			if (sid) {
				savedNameByStaticId[sid] = String(so?.nome ?? "").trim();
				savedDimByStaticId[sid] = {
					altura: (so as any).altura ?? (so as any).height ?? null,
					largura: (so as any).largura ?? (so as any).width ?? null,
					quantity: (so as any).quantity ?? 1,
				};
			}
		}

		const itens: BatchItemPayload[] = itensRaw.map((it, idx) => {
			const indexNum = Number(it.index ?? idx + 1);
			const objetoId = String(it.staticId ?? it.index ?? idx + 1);
			// Caminhos e nomes de arquivo
			const outPathRaw: any =
				typeof it.outputPath === "string"
					? it.outputPath
					: typeof it.caminho_exportado === "string"
						? it.caminho_exportado
						: null;
			const outPath: string | null = outPathRaw ? String(outPathRaw) : null;
			const fileNameFromOutPath = outPath
				? outPath.split("\\").pop() || outPath.split("/").pop()
				: null;
			const fileName =
				typeof it.nome_arquivo_exportado === "string" &&
					it.nome_arquivo_exportado.trim().length > 0
					? it.nome_arquivo_exportado
					: fileNameFromOutPath;
			// Status normalizado
			const statusRaw = String(it.status ?? "unknown");
			const statusNorm =
				statusRaw === "ok"
					? "success"
					: statusRaw === "erro"
						? "error"
						: statusRaw;
			// Nome com fallbacks: name -> savedData -> objeto_id
			const nameFromItem: string | undefined =
				typeof it.name === "string" ? it.name : undefined;
			const nomeFromSaved: string | undefined = savedNameByStaticId[objetoId];
			const nomeFinal: string =
				nameFromItem && nameFromItem.trim()
					? nameFromItem
					: nomeFromSaved && nomeFromSaved.trim()
						? nomeFromSaved
						: objetoId;
			// GUID com fallback pelo mapa de targets
			const guidFinal =
				typeof it.guid === "string" && it.guid
					? it.guid
					: (guidByStaticId[objetoId] ?? null);
			// Tamanhos e hash/duração consolidados
			const tamanhoArquivo =
				typeof it.tamanho_arquivo === "number"
					? it.tamanho_arquivo
					: typeof it.size === "number"
						? it.size
						: null;
			const md5Arquivo = typeof it.md5 === "string" ? it.md5 : null;
			const durationMs =
				typeof it.durationMs === "number" ? it.durationMs : null;

			// Dimensoes do objeto original (para validacao de metragem no backend)
			const dims = savedDimByStaticId[objetoId];

			return {
				index: indexNum,
				objeto_id: objetoId,
				guid: guidFinal,
				nome: nomeFinal,
				page: typeof it.page === "number" ? it.page : undefined,
				largura: dims?.largura ?? null,
				altura: dims?.altura ?? null,
				quantidade: dims?.quantity ?? 1,
				status: statusNorm,
				error: typeof it.error === "string" ? it.error : null,
				nome_arquivo_exportado: fileName,
				caminho_exportado: outPath,
				tamanho_arquivo: tamanhoArquivo,
				md5_arquivo: md5Arquivo,
				duration_ms: durationMs,
			};
		});

		const payload: BatchApiPayload = {
			orc_id: orcId,
			oit_id: oitId,
			tipo_exportacao: String(format || "TIFF").toUpperCase(),
			diretorio_destino: outputDir,
			summary: {
				total: Number(summary.total ?? itens.length),
				success: Number(
					summary.success ?? itens.filter((i) => i.status === "success").length,
				),
				failed: Number(
					summary.failed ?? itens.filter((i) => i.status === "error").length,
				),
				cancelled: Boolean(summary.cancelled),
				output_dir: String(summary.outputDir ?? outputDir),
				duration_ms: Number(
					summary.durationMs ?? batchResult?.metrics?.durationMsTotal ?? 0,
				),
				hash_algorithm:
					typeof summary.hashAlgorithm === "string"
						? summary.hashAlgorithm
						: undefined,
			},
			itens,
		};
		return payload;
	} catch (e) {
		debugLog("ERROR", {
			scope: "buildBatchPayload",
			errorMessage: e instanceof Error ? e.message : String(e),
		});
		return null;
	}
}

function validateBatchPayload(payload: BatchApiPayload): {
	valid: boolean;
	error?: string;
} {
	if (!payload || typeof payload !== "object")
		return { valid: false, error: "Payload ausente" };
	if (!Number.isInteger(payload.orc_id))
		return { valid: false, error: "orc_id inválido (deve ser inteiro)" };
	if (!Number.isInteger(payload.oit_id))
		return { valid: false, error: "oit_id inválido (deve ser inteiro)" };
	if (!payload.tipo_exportacao || typeof payload.tipo_exportacao !== "string")
		return { valid: false, error: "tipo_exportacao é obrigatório" };
	if (!payload.summary || typeof payload.summary !== "object")
		return { valid: false, error: "summary é obrigatório" };
	const s = payload.summary as BatchSummaryPayload;
	if (!Number.isInteger(s.total))
		return { valid: false, error: "summary.total inválido (inteiro)" };
	if (!Number.isInteger(s.success))
		return { valid: false, error: "summary.success inválido (inteiro)" };
	if (!Number.isInteger(s.failed))
		return { valid: false, error: "summary.failed inválido (inteiro)" };
	if (typeof s.cancelled !== "boolean")
		return { valid: false, error: "summary.cancelled inválido (booleano)" };
	if (!Number.isInteger(s.duration_ms))
		return { valid: false, error: "summary.duration_ms inválido (inteiro)" };
	if (!Array.isArray(payload.itens) || payload.itens.length === 0)
		return { valid: false, error: "itens deve conter pelo menos 1 item" };
	for (let i = 0; i < payload.itens.length; i++) {
		const it = payload.itens[i];
		if (!Number.isInteger(it.index))
			return { valid: false, error: `itens[${i}].index inválido (inteiro)` };
		if (!it.nome || typeof it.nome !== "string")
			return { valid: false, error: `itens[${i}].nome é obrigatório` };
		if (!it.status || typeof it.status !== "string")
			return { valid: false, error: `itens[${i}].status é obrigatório` };
	}
	return { valid: true };
}

export async function postBatchExport(
	payload: BatchApiPayload,
	_token: string,
	idempotencyKey?: string,
): Promise<{
	success: boolean;
	data?: any;
	message?: string;
	status?: number;
	details?: any;
}> {
	try {
		// Validação completa local conforme guia
		const validation = validateBatchPayload(payload);
		if (!validation.valid) return { success: false, message: validation.error };

		const idempotency =
			idempotencyKey ||
			((window as any)?.crypto?.randomUUID
				? (window as any).crypto.randomUUID()
				: undefined);
		// Log: exibir payload exato enviado para API no terminal (Vite)
		try {
			debugLog("API_REQUEST", {
				method: "POST",
				url: "/cdr/arquivos/exportar-batch",
				payload,
			});
		} catch { }
		const data: any = await api("/cdr/arquivos/exportar-batch", {
			method: "POST",
			headers: {
				...(idempotency ? { "Idempotency-Key": idempotency } : {}),
			},
			body: JSON.stringify(payload),
		});
		// Considerar sucesso quando backend retorna ok===true ou success===true
		const okFlag = Boolean(
			(data?.success ?? data?.ok ?? false) || data?.result?.batch_id,
		);
		return { success: okFlag, data };
	} catch (e: any) {
		let errorMessage: string;

		if (e instanceof APIError) {
			const code = (e.data?.code || "").toString();
			const entry = (ErrorCatalog as any)[code as ErrorCode];
			errorMessage = entry
				? typeof entry.message === "function"
					? entry.message({})
					: entry.message
				: e.data?.message || e.message || "Erro na API de exportação (batch)";

			// Notificação para erro de exportação
			const { notify } = await import("../../../lib/globalNotify");
			notify({
				type: "error",
				title: "Erro ao Exportar",
				message: "Erro ao exportar. Verifique sua conexão e tente novamente.",
				sticky: true,
			});

			return {
				success: false,
				message: errorMessage,
				status: e.status,
				details: e.data,
			};
		}

		errorMessage =
			e?.message || "Falha na comunicação com API de exportação (batch)";

		// Notificação para erro genérico
		const { notify } = await import("../../../lib/globalNotify");
		notify({
			type: "error",
			title: "Erro ao Exportar",
			message: "Erro ao exportar. Verifique sua conexão e tente novamente.",
			sticky: true,
		});

		return { success: false, message: errorMessage };
	}
}
